import { useEffect, useState } from "react";
import { formatChinaDateTime } from "../time";

import {
  closePlatformSupport,
  getPlatformSupportAttachment,
  getPlatformSupport,
  listPlatformSupport,
  replyPlatformSupport,
  type PlatformSupportDetail,
  type PlatformSupportTicket,
  type PlatformSupportAttachment,
} from "./api";

const PAGE_SIZE = 10;

function statusText(status: PlatformSupportTicket["status"]) {
  return { PENDING: "待客服回复", PROCESSING: "沟通中", RESOLVED: "已关闭" }[status];
}

function PlatformSupportImage({ image }: { image: PlatformSupportAttachment }) {
  const [source, setSource] = useState("");
  useEffect(() => {
    let objectUrl = "";
    void getPlatformSupportAttachment(image.url).then((blob) => {
      objectUrl = URL.createObjectURL(blob);
      setSource(objectUrl);
    }).catch(() => setSource(""));
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [image.url]);
  if (!source) return <span className="platform-support-image-loading">图片加载中…</span>;
  return <a href={source} target="_blank" rel="noreferrer"><img src={source} alt={image.name} /></a>;
}

export function SupportTickets() {
  const [bucket, setBucket] = useState<"open" | "closed">("open");
  const [items, setItems] = useState<PlatformSupportTicket[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState("");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [active, setActive] = useState<PlatformSupportDetail | null>(null);
  const [reply, setReply] = useState("");
  const [images, setImages] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  async function reload() {
    setLoading(true); setError("");
    try {
      const result = await listPlatformSupport({ bucket, query, page, pageSize: PAGE_SIZE });
      setItems(result.items); setTotal(result.total);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "咨询加载失败"); }
    finally { setLoading(false); }
  }

  useEffect(() => { void reload(); }, [bucket, query, page]);

  async function open(ticket: PlatformSupportTicket) {
    setError(""); setReply(""); setImages([]); setConfirmClose(false);
    try { setActive(await getPlatformSupport(ticket.id)); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "咨询详情加载失败"); }
  }

  async function sendReply() {
    if (!active || !reply.trim()) return;
    setSubmitting(true);
    try {
      setActive(await replyPlatformSupport(active.id, reply.trim(), images));
      setReply(""); setImages([]); await reload();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "回复发送失败"); }
    finally { setSubmitting(false); }
  }

  async function closeTicket() {
    if (!active) return;
    setSubmitting(true);
    try {
      await closePlatformSupport(active.id);
      setActive(null); setConfirmClose(false); await reload();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "咨询关闭失败"); }
    finally { setSubmitting(false); }
  }

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  return <section className="platform-subaccounts platform-support" aria-label="客服咨询">
    <div className="platform-panel">
      <div className="platform-order-tabs" role="tablist" aria-label="咨询状态">
        <button className={bucket === "open" ? "is-active" : ""} type="button" onClick={() => { setBucket("open"); setPage(1); }}>待处理</button>
        <button className={bucket === "closed" ? "is-active" : ""} type="button" onClick={() => { setBucket("closed"); setPage(1); }}>已关闭</button>
      </div>
      <form className="platform-list-toolbar" onSubmit={(event) => { event.preventDefault(); setQuery(draft.trim()); setPage(1); }}>
        <div className="platform-list-search"><input aria-label="搜索客服咨询" placeholder="搜索咨询编号、订单号、机构或问题" value={draft} onChange={(event) => setDraft(event.target.value)} /><button type="submit">查询</button>{(draft || query) && <button className="is-secondary" type="button" onClick={() => { setDraft(""); setQuery(""); }}>重置</button>}</div>
      </form>
      <div className="platform-table-wrap"><table className="platform-account-table platform-support-table"><thead><tr><th>咨询编号</th><th>关联订单</th><th>机构 / 提交人</th><th>问题</th><th>状态</th><th>更新时间</th><th>操作</th></tr></thead><tbody>
        {!loading && !error && items.map((item) => <tr key={item.id}><td><strong>{item.id.slice(0, 12).toUpperCase()}</strong></td><td>{item.order_number ?? "未关联订单"}<small>{item.order_number ? `${item.product_name} · ${item.subject_summary}` : "—"}</small></td><td>{item.institution_name}<small>{item.submitter_name}</small></td><td className="platform-support-question">{item.question_summary}</td><td><span className={`platform-status is-${item.status.toLowerCase()}`}>{statusText(item.status)}</span></td><td>{formatChinaDateTime(item.latest_message_at)}</td><td><div className="platform-row-actions"><button type="button" onClick={() => void open(item)}>查看沟通</button></div></td></tr>)}
        {loading && <tr><td className="platform-table-state" colSpan={7}>正在加载咨询…</td></tr>}
        {!loading && error && <tr><td className="platform-table-state is-error" colSpan={7}>{error}</td></tr>}
        {!loading && !error && !items.length && <tr><td className="platform-table-state" colSpan={7}>{bucket === "open" ? "暂无待处理咨询" : "暂无已关闭咨询"}</td></tr>}
      </tbody></table></div>
      <footer className="platform-pagination"><span>共 {total} 条，每页 {PAGE_SIZE} 条</span><div><button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>上一页</button><span>{page} / {pageCount}</span><button type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)}>下一页</button></div></footer>
    </div>
    {active && <div className="platform-modal-layer"><section className="platform-modal platform-support-modal" role="dialog" aria-modal="true" aria-label="客服咨询详情">
      <header><div><h2>客服咨询</h2><p>{active.institution_name} · {active.order_number ?? "未关联订单"}{active.product_name ? ` · ${active.product_name}` : ""}</p></div><button className="platform-modal-close" type="button" aria-label="关闭" onClick={() => setActive(null)}>×</button></header>
      <div className="platform-support-conversation">{active.messages.map((message) => <article className={`platform-support-message is-${message.sender.toLowerCase()}`} key={message.id}><div><strong>{message.sender_name}</strong><time>{formatChinaDateTime(message.created_at)}</time></div><p>{message.body}</p>{message.attachments.length > 0 && <div className="platform-support-images">{message.attachments.map((image) => <PlatformSupportImage image={image} key={image.id} />)}</div>}</article>)}</div>
      {active.status !== "RESOLVED" ? <footer className="platform-support-reply"><textarea aria-label="客服回复" placeholder="请输入回复内容" value={reply} onChange={(event) => setReply(event.target.value)} /><div className="platform-support-reply-actions"><label className="platform-file-button">添加图片<input type="file" accept="image/*" multiple hidden onChange={(event) => setImages(Array.from(event.target.files ?? []).slice(0, 10))} /></label><span>已选择 {images.length} 张，最多 10 张</span><button className="is-secondary" type="button" onClick={() => setConfirmClose(true)}>关闭咨询</button><button type="button" disabled={submitting || !reply.trim()} onClick={() => void sendReply()}>发送回复</button></div></footer> : <footer className="platform-support-closed">本次咨询已关闭，客户如仍有问题可重新发起咨询。</footer>}
      {confirmClose && <div className="platform-confirm-layer"><section role="alertdialog" aria-modal="true" aria-label="确认关闭咨询"><h3>确认关闭咨询？</h3><p>关闭后双方都不能继续回复。</p><div><button className="is-secondary" type="button" onClick={() => setConfirmClose(false)}>取消</button><button type="button" disabled={submitting} onClick={() => void closeTicket()}>确认关闭</button></div></section></div>}
    </section></div>}
  </section>;
}
